import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api";

export function useOnboarding() {
  const { user, updateUser } = useAuth();

  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.onboardingCompleted) {
      setLoading(false);
      return;
    }

    const checkConditions = async () => {
      try {
        const hasAvatar = !!user.avatar;
        const stats = await api.getMyNotesStats();
        const hasNotes = stats.data.all > 0;

        const applicableSteps = [];
        if (!hasAvatar) applicableSteps.push("welcome");
        if (!hasNotes) {
          applicableSteps.push("createNote");
          applicableSteps.push("exploreMap");
        }

        if (applicableSteps.length === 0) {
          // User already has avatar and notes — auto-complete
          await updateUser({ onboardingCompleted: true });
        } else {
          setSteps(applicableSteps);
          setShowOnboarding(true);
        }
      } catch {
        // If stats check fails, don't block the user — skip onboarding
      } finally {
        setLoading(false);
      }
    };

    checkConditions();
  }, []);

  const completeOnboarding = useCallback(async () => {
    setShowOnboarding(false);
    await updateUser({ onboardingCompleted: true });
  }, [updateUser]);

  const nextStep = useCallback(() => {
    if (currentStepIndex >= steps.length - 1) {
      completeOnboarding();
    } else {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [currentStepIndex, steps.length, completeOnboarding]);

  const prevStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback(
    (index) => {
      if (index >= 0 && index < steps.length) {
        setCurrentStepIndex(index);
      }
    },
    [steps.length],
  );

  const pauseOnboarding = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeOnboarding = useCallback(() => {
    setIsPaused(false);
  }, []);

  return {
    showOnboarding,
    steps,
    currentStepIndex,
    currentStep: steps[currentStepIndex] || null,
    totalSteps: steps.length,
    isPaused,
    loading,
    nextStep,
    prevStep,
    goToStep,
    skipStep: nextStep,
    pauseOnboarding,
    resumeOnboarding,
    completeOnboarding,
  };
}
